import { ParsedUrlQuery } from 'querystring'
import { GetStaticPropsResult, GetStaticPathsResult } from 'next'
import dynamic from 'next/dynamic'

import { casePageModuleQuery } from '@data/sanity/queries/page'
import { SanitySiteFragment } from '@data/sanity/queries/types/site'
import { SanityCasePage, SanityPageType } from '@data/sanity/queries/types/page'
import { getAllLocales, getLocaleLanguage, Locale } from '@lib/language'
import {
  BasePageProps,
  getPagePropsFromContext,
  getPageSanityClient,
} from '@lib/page'
import { CustomStaticPropsContext, StaticPathWithSlug } from '@lib/routes'
import { getSanityClient } from '@lib/sanity/client'
import {
  defaultModuleQueryParameters,
  getAllSlugs,
  getCasePage,
} from '@lib/sanity/page'

import Layout from '@components/layout'
import Modules from '@modules/modules'
const PreviewModules = dynamic(() => import('@modules/preview-modules'))

type CasePageProps = BasePageProps & {
  slug: string
  page: SanityCasePage
  site: SanitySiteFragment
}

const CasePage = ({ draftMode, locale, slug, page, site }: CasePageProps) => {
  return (
    <Layout page={page} site={site} draftMode={draftMode}>
      {!draftMode && <Modules />}
      {draftMode && (
        <PreviewModules
          query={casePageModuleQuery}
          params={{
            ...defaultModuleQueryParameters,
            locale,
            caseSlug: slug,
          }}
        />
      )}
    </Layout>
  )
}

interface StaticCasePageParams extends ParsedUrlQuery {
  slug: string
}

export async function getStaticProps(
  context: CustomStaticPropsContext<StaticCasePageParams>,
): Promise<GetStaticPropsResult<CasePageProps>> {
  const locale = getLocaleLanguage(context.locale)

  // Sanity data
  const sanityClient = getPageSanityClient(!!context.draftMode)
  const slug = context.params?.slug ?? ''
  const page = await getCasePage(sanityClient, locale, slug)

  if (!page.page || page.page.type === SanityPageType.TEMPLATE) {
    return {
      notFound: true,
      revalidate: 60,
    }
  }

  return {
    props: {
      ...getPagePropsFromContext(context),
      slug,
      page: page.page,
      site: page.site,
    },
    revalidate: 60,
  }
}

export async function getStaticPaths(): Promise<
  GetStaticPathsResult<StaticCasePageParams>
> {
  const sanityClient = getSanityClient()
  const allPages = await getAllSlugs(sanityClient, 'case')
  const allLocales = getAllLocales()
  const paths: StaticPathWithSlug[] = allPages
    .map((page) => ({ ...page, locale: page.locale as Locale }))
    .filter(({ locale }) => allLocales.includes(locale))
    .filter(({ slug }) => !!slug)
    .map(({ locale, slug }) => ({
      params: { slug },
      locale,
    }))

  return {
    paths,
    fallback: 'blocking',
  }
}

export default CasePage
